"use client";
import React, { useRef, useState } from "react";
import { motion, useInView } from "motion/react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import { Button } from "@/components/ui/button";
import GetInTouch from "@/components/modals/get-in-touch";

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

interface PageCtaProps {
  title: React.ReactNode;
  description: string;
  primaryLabel?: string;
}

const PageCta = ({
  title,
  description,
  primaryLabel = "Start a Project",
}: PageCtaProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });
  const [isGetInTouchOpen, setIsGetInTouchOpen] = useState(false);

  return (
    <section ref={sectionRef} className="section pb-[60px] lg:pb-[140px] relative">
      {/* Background blobs */}
      <div
        className="absolute left-[10%] top-[20%] w-[600px] h-[600px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.12)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none animate-shape-pulse"
        />
      <div
        className="absolute right-[5%] bottom-[10%] w-[400px] h-[400px] bg-[linear-gradient(180deg,_rgba(98,42,173,0.15)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none animate-shape-pulse"
        />

      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div variants={itemVariants}>
            <AnimatedCard
              className="rounded-[22px] lg:rounded-[30px]"
              circleClassName="w-[200px] h-[200px] blur-[100px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[22px] lg:rounded-[30px]"
                className="rounded-[22px] lg:rounded-[30px] bg-gradient-to-b from-[#141128] to-[#0e0821] p-[28px] sm:p-[44px] lg:p-[80px] relative overflow-hidden text-center"
              >
                {/* Inner glows */}
                <div
                  className="absolute top-[-20%] left-[50%] -translate-x-1/2 w-[500px] h-[300px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.2)_0%,_rgba(135,82,250,0)_100%)] rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />
                <div
                  className="absolute bottom-[-10%] left-[10%] w-[300px] h-[300px] bg-[linear-gradient(180deg,_rgba(98,42,173,0.2)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[80px] pointer-events-none animate-shape-pulse"
                  />
                <div
                  className="absolute bottom-[-10%] right-[10%] w-[250px] h-[250px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.15)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[70px] pointer-events-none animate-shape-pulse"
                  />

                <div className="relative z-10">
                  <h2 className="text-[32px] md:text-[40px] lg:text-[52px] font-semibold leading-[1.15] max-w-[14ch] md:max-w-[18ch] lg:max-w-[17ch] mx-auto text-balance mb-[20px]">
                    {title}
                  </h2>
                  <p className="text-[15px] lg:text-[17px] font-light leading-[1.7] text-foreground/90 max-w-[340px] md:max-w-[500px] lg:max-w-[700px] mx-auto mb-[40px]">
                    {description}
                  </p>
                  <div className="flex justify-center items-center">
                    <Button
                      variant="theme"
                      className="h-[48px] rounded-[14px] px-[16px] md:px-[28px] text-[14px] md:text-[16px] shadow-none cursor-pointer"
                      onClick={() => setIsGetInTouchOpen(true)}
                    >
                      {primaryLabel}
                    </Button>
                  </div>
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
        </motion.div>
      </div>

      <GetInTouch open={isGetInTouchOpen} onOpenChange={setIsGetInTouchOpen} />
    </section>
  );
};

export default PageCta;
